import { NetworkStatus } from '@apollo/client';
import { PagesForTelemetry } from '@apps/registry/common/components/Catalog';
import { CatalogItemTile } from '@apps/registry/common/components/Catalog/Catalog.types';
import { useGetRecommendedCatalogProductsQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useMemo } from 'react';
import { RawRecommendedCatalogProductItemGroup, RecommendedCatalogProductItemGroup } from '../RecommendedProductsSection.types';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useCatalogLoggingContext } from '@apps/registry/common/components/Catalog/Catalog.logging';

type UseRecommendCatalogProductsArgs = {
  eventId: Maybe<string>;
  pageSize: number;
  telemetryPageValue: PagesForTelemetry;
  skip?: boolean;
};

const isValidEntityItemGroupObject = (val: RawRecommendedCatalogProductItemGroup): val is RecommendedCatalogProductItemGroup => {
  return !!val?.id;
};

export const useGetRecommendedCatalogProducts = ({ eventId, pageSize, telemetryPageValue, skip }: UseRecommendCatalogProductsArgs) => {
  const { currentStorefront } = useCatalogRoutePaths();
  const { data, loading, error, fetchMore, networkStatus } = useGetRecommendedCatalogProductsQuery({
    batchMode: 'fast',
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip,
    variables: {
      eventId,
      first: pageSize,
      storefront: currentStorefront,
    },
  });

  const { trackLoadMoreProductsButtonClicked } = useCatalogLoggingContext();

  const pageInfo = data?.recommendCatalogHomeSections?.sections?.[0]?.paginatedCatalogEntities?.pageInfo;

  const handleOnLoadMoreProductsButtonClick = useEventCallback(() => {
    if (pageInfo?.hasNextPage) {
      trackLoadMoreProductsButtonClicked({
        method: 'load more button',
        component: 'productRec',
        page: telemetryPageValue,
        requestSize: pageSize,
        productCount: products.length,
        startCursor: pageInfo.startCursor,
        endCursor: pageInfo.endCursor,
      });

      fetchMore && fetchMore({ variables: { endCursor: pageInfo.endCursor } });
    }
  });

  const products = useMemo<CatalogItemTile[]>(() => {
    const items: CatalogItemTile[] = [];

    data?.recommendCatalogHomeSections?.sections?.[0]?.paginatedCatalogEntities.edges.forEach(edge => {
      if (edge.node.__typename === 'ProductCatalogEntityItemGroup' && isValidEntityItemGroupObject(edge.node.catalogItemGroupData)) {
        if (edge.node.catalogItemGroupData?.tileViewItem?.__typename === 'ProductCatalogItem') {
          items.push(edge.node.catalogItemGroupData.tileViewItem);
        }
      }
    });

    return items || [];
  }, [data]);

  return {
    products,
    loading,
    error,
    onLoadMore: handleOnLoadMoreProductsButtonClick,
    hasNextPage: !!pageInfo?.hasNextPage,
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
    totalCount: pageInfo?.totalCount || 0,
  };
};
